/**
 * Campo "Local" da atividade: texto livre que, quando o Maps está disponível,
 * sugere lugares enquanto a pessoa digita.
 *
 * Escolher uma sugestão preenche o nome E o lugar estruturado (id, endereço,
 * coordenadas). Voltar a digitar desfaz o lugar estruturado: o nome deixou de
 * ser o do Google, e coordenadas de outro lugar seriam mentira no mapa.
 *
 * Sem Maps (sem chave, sem rede), é um campo de texto — nada some.
 */
import { useEffect, useId, useRef, useState } from 'react';

import { Icon } from '../../ui/icons';
import {
  buscarSugestoes,
  detalharLugar,
  type LugarEscolhido,
  novaSessaoDeBusca,
  type SessaoDeBusca,
  type Sugestao,
} from '../../maps/places';

export interface LugarDaAtividade {
  placeId: string;
  placeAddress: string | null;
  latitude: number;
  longitude: number;
}

interface Props {
  nome: string;
  lugar: LugarDaAtividade | null;
  onChange: (nome: string, lugar: LugarDaAtividade | null) => void;
  /** Rótulo do campo. Padrão "LOCAL". */
  rotulo?: string;
  /**
   * O que a escolha põe no texto: o nome do lugar (atividade) ou o endereço
   * (hospedagem — o nome dela é outro campo).
   */
  modo?: 'nome' | 'endereco' | 'destino';
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

const MINIMO_PARA_BUSCAR = 3;
const ESPERA_MS = 300;

export function LocalField({ nome, lugar, onChange, rotulo = 'Local', modo = 'nome', disabled, placeholder, required }: Props) {
  const listaId = useId();
  const [sugestoes, setSugestoes] = useState<Sugestao[]>([]);
  const [aberto, setAberto] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const sessao = useRef<SessaoDeBusca | null>(null);
  const ultimaBusca = useRef(0);
  // Só busca depois que a pessoa digita: um valor que já vem preenchido
  // (Configurações) não abre a lista sozinho (PO, 19/09).
  const [digitou, setDigitou] = useState(false);

  // Uma sessão de busca por "digitar → escolher": é assim que o Google cobra
  // as sugestões como parte de uma consulta só.
  async function sessaoAtual(): Promise<SessaoDeBusca | null> {
    if (!sessao.current) sessao.current = await novaSessaoDeBusca();
    return sessao.current;
  }

  useEffect(() => {
    if (lugar || !digitou || nome.trim().length < MINIMO_PARA_BUSCAR) {
      setSugestoes([]);
      setAberto(false);
      return;
    }
    const marca = ++ultimaBusca.current;
    const timer = setTimeout(() => {
      void (async () => {
        const s = await sessaoAtual();
        if (!s) return;
        setBuscando(true);
        try {
          const resultado = await buscarSugestoes(nome.trim(), s, modo === 'destino');
          // Resposta de uma digitação antiga não pode sobrescrever a atual.
          if (marca === ultimaBusca.current) {
            setSugestoes(resultado);
            setAberto(resultado.length > 0);
          }
        } catch {
          if (marca === ultimaBusca.current) setSugestoes([]);
        } finally {
          if (marca === ultimaBusca.current) setBuscando(false);
        }
      })();
    }, ESPERA_MS);
    return () => clearTimeout(timer);
  }, [nome, lugar, modo, digitou]);

  async function escolher(sugestao: Sugestao) {
    setAberto(false);
    setSugestoes([]);
    // Destino da viagem: só o texto ("Lisboa, Portugal"), sem lugar
    // estruturado — a viagem não guarda coordenadas do destino (e não há
    // consulta de detalhes, que é o que o Google cobra mais caro).
    if (modo === 'destino') {
      sessao.current = null;
      setDigitou(false);
      onChange(sugestao.secundario ? `${sugestao.principal}, ${sugestao.secundario}` : sugestao.principal, null);
      return;
    }
    let detalhe: LugarEscolhido | null = null;
    try {
      detalhe = await detalharLugar(sugestao);
    } catch {
      detalhe = null;
    }
    // Sessão consumida: a próxima digitação abre outra.
    sessao.current = null;
    if (!detalhe) {
      onChange(sugestao.principal, null);
      return;
    }
    onChange(modo === 'endereco' ? (detalhe.endereco ?? detalhe.nome) : detalhe.nome, {
      placeId: detalhe.placeId,
      placeAddress: detalhe.endereco,
      latitude: detalhe.latitude,
      longitude: detalhe.longitude,
    });
  }

  return (
    <div className="local">
      <label className="field">
        <span className="field__label">{rotulo}</span>
        <input
          className="field__input"
          value={nome}
          disabled={disabled}
          placeholder={placeholder}
          required={required}
          autoComplete="off"
          role="combobox"
          aria-expanded={aberto}
          aria-controls={listaId}
          aria-autocomplete="list"
          onChange={(e) => {
            setDigitou(true);
            onChange(e.target.value, null);
          }}
          onFocus={() => sugestoes.length > 0 && setAberto(true)}
          onBlur={() => setTimeout(() => setAberto(false), 150)}
        />
        {lugar && (
          <span className="local__escolhido">
            <Icon name="local" size={14} />
            <span className="local__endereco">{modo === 'endereco' ? 'No mapa' : (lugar.placeAddress ?? 'Lugar no mapa')}</span>
            <button
              type="button"
              className="local__limpar"
              aria-label="Remover lugar do mapa"
              onClick={() => onChange(nome, null)}
            >
              ×
            </button>
          </span>
        )}
        {buscando && !lugar && <span className="local__status">Buscando…</span>}
      </label>
      {aberto && sugestoes.length > 0 && (
        <ul className="local__sugestoes" id={listaId} role="listbox">
          {sugestoes.map((s) => (
            <li key={s.predicao.placeId ?? s.principal + s.secundario} role="option" aria-selected={false}>
              <button type="button" className="local__sugestao" onMouseDown={(e) => e.preventDefault()} onClick={() => void escolher(s)}>
                <span className="local__principal">{s.principal}</span>
                {s.secundario && <span className="local__secundario">{s.secundario}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
